import { OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, setDisplay, toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { createHtmlElement, dispatchEventOnInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService, VALIDATION_SERVICE, ValidationService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { ValidationError } from "@cervval.js/common/src/forms/validationError";
import { anyReallyAny, asAnyObject, getProp } from "@cervval.js/common/src/tools/any.tools";
import { wrapAsArray } from "@cervval.js/common/src/tools/collection.array.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { Tooltip } from "./../bootstrap";

/** Class applied to editor element in multiple values state */
export const MULTIPLE_VALUES_CSS_CLASS = "multiple-values";

/** Option type of setValue for editors */
export type EditorSetValueOptions = { refreshIfChanged?: boolean, dispatchEvents?: boolean };

/** 
 * Indicates where the message error should be displayed. 
 * UNDERLINE corresponds to an error message displayed under the editor
 * TOOLTIP corresponds to an error message displayed in a tooltip when editor is hovered
 * Default mode is UNDERLINE 
 * */
export enum ErrorMessageDisplayMode {
    UNDERLINE = 1,
    TOOLTIP
}

/** Options of Editor */
export interface EditorOptions {

    /** Disable input of editor */
    disabled: ValueOrFunction<boolean>;

    /** Apply specific style if multiple values (linked with GeneratedForm component) */
    multipleValuesState: boolean;
}

export const defaultEditorOptions: EditorOptions = {

    /** Disable input of editor */
    disabled: false,

    /** Apply specific style if multiple values (linked with GeneratedForm component) */
    multipleValuesState: false
};

/** Type of detail in event dispatched for ON_CHANGE  */
export type EditorOnChangeEvent<T> = {

    /** Value of editor */
    value: T
}

export abstract class CjsEditor<T, I18n = unknown> extends WebComponent<I18n> {

    /** Logger */
    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    /** 
     * Triggered when value of editor has change 
     */
    @Event(NoBubblesEventInit)
    public changeEvent!: EventHandler<EditorOnChangeEvent<T>>;

    /** Validation */
    @Injected(VALIDATION_SERVICE)
    protected _validation!: ValidationService;

    /** Attributes options of editor, defined and decorated in child class */
    public readonly abstract options: WebComponentOptions<EditorOptions>;

    /** get the value of disabled property of options  */
    public get disabled(): boolean {
        return Boolean(getVOFValue(this.options.disabled));
    }

    /** Element where error text will be displayed if display mode is "underline" */
    protected _validationErrorMessageElement: HTMLDivElement;
    /** Tooltip where error text will be displayed if display mode is "tooltip" */
    protected _validationErrorMessageTooltip: Tooltip | undefined = undefined;
    /** Options for _validationErrorMessageTooltip, is public to enable tooltip customization */
    public validationTooltipOptions: Partial<Tooltip.Options> = {};

    /**
     * Value in input
     */
    protected _value: T | undefined = undefined;

    /**
     * Get input of Editor, where user set data
     */
    public abstract getInput(): HTMLElement;

    /**
     * Editor constructor
     */
    constructor(customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);

        // Create validation error message div
        this._validationErrorMessageElement = this._createErrorElement();
    }

    /** Called once after component is added to DOM */
    public override init(): void {
    }

    /** Refresh component */
    public override refresh(): void {
        // Refresh disabled state
        this._refreshDisabled();

        // Update multiple 
        this._toggleMultipleValuesClass(this.getValidationFormElement());
    }

    /** 
     * Set editor value 
     * @returns true if value changed
     */
    public setValue(value: T | undefined, options: EditorSetValueOptions = { dispatchEvents: true, refreshIfChanged: true }): boolean {

        if (this._value !== value) {
            this._value = value;

            // reformat value in input if needed
            this._refreshInputValue();

            if (options.refreshIfChanged) {
                this.refresh();
            }

            if (options.dispatchEvents) {
                this._dispatchValueChangedEvent();
            }
            return true;
        }
        return false;
    }

    /** Get editor value */
    public getValue(): T | undefined {
        return this._value;
    }


    /**
     * Set displayed error after validating editor value
     * @param error 
     */
    public setValidationError(error: ValidationError | undefined, displayMode: ErrorMessageDisplayMode = ErrorMessageDisplayMode.UNDERLINE): void {
        if (!error) {
            this._removeValidationError(this._validationErrorMessageElement, this.getValidationFormElement(), displayMode);
        } else {
            if (this._validationErrorMessageElement) {
                // Add "warning" css class if error is a warning
                toggleClass(this._validationErrorMessageElement, Boolean(error.isWarning), "warning-error");

                // Set error message
                this._validationErrorMessageElement.innerHTML = error.errorMessage;
            }
            this._addValidationError(this._validationErrorMessageElement, this.getValidationFormElement(), displayMode, error.isWarning);
        }
    }

    /**
     * @returns validationFormElement
     * Where "is-invalid" class will be added / removed
     */
    public getValidationFormElement(): HTMLElement | HTMLElement[] {
        return this.getInput();
    }

    /**
     * Calls select on the input if available (select the text in the input)
     */
    public select(): void {
        const input = this.getInput();
        if (typeof asAnyObject(input)["select"] === "function") {
            asAnyObject(input)["select"]();
        }
    }

    /**
     * Get caret position (only works for text editors), returns -1 otherwise
     */
    public get selectionStart(): number {
        const input = this.getInput();
        if (typeof asAnyObject(input)["selectionStart"] === "number") {
            return asAnyObject(input)["selectionStart"]();
        }
        return -1;
    }

    /**
     * @returns true if caret is at the end of the input current value (only works for text editors)
     */
    public isCaretAtTheEnd(): boolean {
        const caretPosition = this.selectionStart;
        if (caretPosition >= 0) {
            const currentValue = this._getValueFromInputComponent();
            if (typeof currentValue === "string") {
                return caretPosition >= String(currentValue).length;
            }
        }
        return false;
    }

    /**
     * Redirect focus on input
     */
    public override focus(options?: FocusOptions): void {
        this.getInput().focus(options);
    }

    /**
     * Dispatch event on input
     */
    public dispatchEventOnInput(event: Event): boolean {
        return dispatchEventOnInput(this.getInput(), event);
    }

    /** 
     * Trigger the edition then calls onDone when value has changed.
     * eg: Open a select, than call onDone when value has been selected.
     * 
     * May not be supported by all editors. 
     * Override this function if your editor has the capacity to trigger edition and detect when edition is finished.
     */
    public tryEdit(onDone: () => void): void {
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected abstract _getValueFromInputComponent(): T | undefined;

    /**
     * Update the value of the input from _value of the Editor
     */
    protected abstract _refreshInputValue(): void;

    /**
     * Update disabled attribute on input component
     */
    protected abstract _refreshDisabled(): void;

    /**
     * Sets _value from input value and triggers onValueChange if value changed
     * Is triggered by editor when input value changes
     */
    protected _onInputValueChange(event?: Event | anyReallyAny): void {
        const newValue = this._getValueFromInputComponent();
        if (this._validateInputValue(newValue)) {
            this._setValueIfDifferentAndDispatchEvents(newValue);
        }
    }

    /**
     * Decorate the input depending on input value validity
     * @returns false if newValue is invalid
     */
    protected _validateInputValue(newValue: T | undefined): boolean {
        return true;
    }

    /**
     * Sets _value from input value and triggers onValueChange if value changed
     * Is triggered by editor when input value changes
     */
    protected _setValueIfDifferentAndDispatchEvents(newValue: T | undefined): boolean {
        if (newValue !== null && (newValue !== this._value || this.options.multipleValuesState)) {
            this._value = newValue;

            // dispatch events
            this._dispatchValueChangedEvent();
            return true;
        }
        return false;
    }

    /**
     * Display error if input is invalid
     * @param errorMessageElement 
     * @param editorElement
     * @param displayMode
     * @param isWarning : use validator only as a warning
     */
    protected _addValidationError(errorMessageElement?: HTMLElement, editorElement?: HTMLElement | HTMLElement[], displayMode: ErrorMessageDisplayMode = ErrorMessageDisplayMode.UNDERLINE, isWarning?: boolean): void {
        if (errorMessageElement) {
            toggleClass(errorMessageElement, Boolean(isWarning), "warning-error");
            setDisplay(errorMessageElement, Display.BLOCK);
        }

        if (editorElement) {
            if (Boolean(isWarning)) {
                if (Array.isArray(editorElement)) {
                    editorElement.forEach(elt => elt.classList.remove("is-invalid", "warning-error"));
                } else {
                    editorElement.classList.add("warning-error");
                }
            }
            if (Array.isArray(editorElement)) {
                editorElement.forEach(elt => elt.classList.remove("is-invalid", "warning-error"));
            } else {
                editorElement.classList.add("is-invalid");
            }
        }
        if (errorMessageElement) {
            switch (displayMode) {
                case ErrorMessageDisplayMode.TOOLTIP:
                    if (this._validationErrorMessageTooltip && getProp(this._validationErrorMessageTooltip, "_element")) { this._validationErrorMessageTooltip.dispose(); } // remove tooltip if one is already set
                    this.validationTooltipOptions.title = errorMessageElement.innerHTML;
                    this.validationTooltipOptions.customClass = "errorTooltip";
                    this._validationErrorMessageTooltip = new Tooltip(this, this.validationTooltipOptions);
                    break;

                default: // underline mode
                    setDisplay(errorMessageElement, Display.BLOCK);
                    this.append(errorMessageElement);
                    break;
            }
        }

    }

    /**
     * Remove displayed error once input is valid
     * @param errorMessageElement 
     * @param editorElement 
     */
    protected _removeValidationError(errorMessageElement?: HTMLElement, editorElement?: HTMLElement | HTMLElement[], displayMode: ErrorMessageDisplayMode = ErrorMessageDisplayMode.UNDERLINE): void {
        if (errorMessageElement) {
            switch (displayMode) {
                case ErrorMessageDisplayMode.TOOLTIP:
                    if (this._validationErrorMessageTooltip && getProp(this._validationErrorMessageTooltip, "_element")) {
                        this._validationErrorMessageTooltip.dispose();
                        this._validationErrorMessageTooltip = undefined;
                    }
                    break;

                default: // underline display mode
                    errorMessageElement.remove();
                    break;
            }
        }
        if (editorElement) {
            if (Array.isArray(editorElement)) {
                editorElement.forEach(elt => elt.classList.remove("is-invalid", "warning-error"));
            } else {
                editorElement.classList.remove("is-invalid", "warning-error");
            }
        }
    }

    /**
     * Creates the error div to contain validation message as all editors do not have HTML templates
     */
    protected _createErrorElement(): HTMLDivElement {
        const validationErrorMessageElement = createHtmlElement("div", {
            cssClass: ["input-error-message", "invalid-feedback"]
        });

        return validationErrorMessageElement;
    }

    /**
     * Add/Remove multiple values class
     */
    protected _toggleMultipleValuesClass(element: HTMLElement | HTMLElement[] | null): void {
        wrapAsArray(element).forEach(elt => toggleClass(elt, this.options.multipleValuesState, MULTIPLE_VALUES_CSS_CLASS));
    }

    /**
     * Dispatch value change event
     * @param value [optional] for editors who need to dispatch other type of value than their this._value
     */
    protected _dispatchValueChangedEvent(value?: anyReallyAny): void {
        this.changeEvent.emit({
            value: value ?? this._value
        });
    }

}